import { Scores } from "model/Scores";
import { TrackRound } from "model/Match/Track/TrackRound";
import { KnockoutRoundResult } from "model/Match/Set/Knockout/Track/KnockoutRoundResult";
import { KnockoutRound } from "model/Match/Set/Knockout/Track/KnockoutRound";
import {
  getLivesLost,
  getMatchingLivesRules,
  LivesRules,
} from "model/Match/Set/Knockout/LivesRules";
import { nonUndefined } from "Utility";
import { Roster, getRosterId, getRosterDriverIds } from "model/Roster/Roster";
import { RosterScope } from "model/Roster/RosterScope";

export const mapKnockoutRounds = (
  baseRounds: TrackRound[],
  rosters: Roster[],
  rosterScope: RosterScope,
  trackNum: number,
  runningLives: Scores,
  livesRules: LivesRules[],
  noKoRound: boolean,
  startingLives: number,
  isNewGolden: boolean,
  goldenLivesWinners: string[]
) => {
  return nonUndefined(
    baseRounds.map((r, roundNum) =>
      mapKnockoutRound(
        r,
        rosters,
        rosterScope,
        trackNum,
        runningLives,
        livesRules,
        noKoRound && trackNum === 1 && roundNum === 0,
        startingLives,
        isNewGolden,
        goldenLivesWinners
      )
    )
  );
};

const mapKnockoutRound = (
  base: TrackRound,
  rosters: Roster[],
  rosterScope: RosterScope,
  trackNum: number,
  runningLives: Scores,
  livesRules: LivesRules[],
  isNoKoRound: boolean,
  startingLives: number,
  isNewGolden: boolean,
  goldenLivesWinners: string[]
): KnockoutRound | undefined => {
  const results: KnockoutRoundResult[] = [];
  const numDriversLeft = Object.keys(runningLives).filter(
    (l) => runningLives[l] > 0
  ).length;

  const numLivesLeft = Object.values(runningLives)
    .filter((l) => l > 0)
    .reduce((a, b) => a + b, 0);
  const matchingRules = getMatchingLivesRules(
    livesRules,
    numDriversLeft,
    numLivesLeft,
    trackNum
  );

  if (!matchingRules) {
    return undefined;
  }

  for (let i = 0; i < rosters.length; i++) {
    const roster = rosters[i];
    const rosterId = getRosterId(roster);
    if (!rosterId) {
      continue;
    }

    const livesAtStart = runningLives[rosterId];
    if (livesAtStart <= 0) {
      continue;
    }

    const rosterDriverIds = getRosterDriverIds(roster, rosterScope) ?? [];
    const rosterTimes = base.times.filter((t) =>
      rosterDriverIds.includes(t.driverId)
    );

    const worstIsDnf =
      rosterTimes.length === 0 ||
      rosterTimes.some((t) => !!t.finishTimeMs && t.finishTimeMs < 0);
    const worstPos = worstIsDnf
      ? numDriversLeft
      : Math.max(0, ...rosterTimes.map((t) => t.position));
    let livesLost = isNoKoRound
      ? 0
      : getLivesLost(matchingRules, numDriversLeft, worstPos);

    if (base.golden && worstPos === 1) {
      if (isNewGolden) {
        if (!goldenLivesWinners.includes(rosterId)) {
          goldenLivesWinners.push(rosterId);
          livesLost = -1;
        }
      } else if (livesAtStart < startingLives) {
        livesLost = -1;
      }
    }

    results.push({
      livesAtStart,
      livesLost,
      rosterId,
    });

    const remainingLives = livesAtStart - livesLost;
    if (remainingLives <= 0) {
      runningLives[rosterId] = numDriversLeft * -1;
    } else {
      runningLives[rosterId] = livesAtStart - livesLost;
    }
  }

  return {
    ...base,
    results,
  };
};
